import { useCallback, useEffect, useRef, useState } from 'react';
const DRAWER_WIDTH_KEY = 'drawer-width';
const RESIZE_DEBOUNCE_MS = 150;
/**
 * Get the maximum allowed drawer width based on viewport size and responsive mode
 */ const getMaxAllowedWidth = (viewportWidth, responsive, maxWidth)=>{
    if (responsive) {
        // Mobile: full width
        if (viewportWidth < 768) {
            return viewportWidth;
        }
        // Tablet: 70% of viewport
        if (viewportWidth < 1024) {
            return Math.floor(viewportWidth * 0.7);
        }
    }
    // Desktop or non-responsive: 90% of viewport, capped at maxWidth
    return Math.min(Math.floor(viewportWidth * 0.9), maxWidth);
};
/**
 * Clamp width to valid range based on constraints
 */ const clampWidth = (width, minWidth, maxWidth, responsive, viewportWidth)=>{
    const maxAllowed = getMaxAllowedWidth(viewportWidth, responsive, maxWidth);
    return Math.max(minWidth, Math.min(maxAllowed, width));
};
/**
 * Hook to handle drawer resize functionality
 * - Manual resize via drag handle
 * - Automatic adjustment on window resize
 * - Persists width to localStorage
 * - Responsive breakpoints (optional)
 */ export const useDrawerResize = (options = {})=>{
    const { minWidth = 300, maxWidth = 800, defaultWidth = 560, onWidthChange, responsive = false } = options;
    const [handleElement, setHandleElement] = useState(null);
    const isResizingRef = useRef(false);
    const startXRef = useRef(0);
    const startWidthRef = useRef(0);
    const currentWidthRef = useRef(defaultWidth);
    const resizeTimeoutRef = useRef(null);
    // Callback ref to capture the handle element
    const resizeHandleRef = useCallback((node)=>{
        setHandleElement(node);
    }, []);
    // Restore width from localStorage on mount
    useEffect(()=>{
        let initialWidth = defaultWidth;
        try {
            const storedWidth = localStorage.getItem(DRAWER_WIDTH_KEY);
            if (storedWidth) {
                const parsed = Number(storedWidth);
                if (!Number.isNaN(parsed)) {
                    initialWidth = parsed;
                }
            }
        } catch  {
        // localStorage access failed - use default width
        }
        // Clamp to current constraints
        const viewportWidth = window.innerWidth;
        const clampedWidth = clampWidth(initialWidth, minWidth, maxWidth, responsive, viewportWidth);
        currentWidthRef.current = clampedWidth;
        onWidthChange?.(clampedWidth);
    }, [
        defaultWidth,
        maxWidth,
        minWidth,
        onWidthChange,
        responsive
    ]); // Only run on mount
    // Save width to localStorage
    const persistWidth = useCallback((width)=>{
        try {
            localStorage.setItem(DRAWER_WIDTH_KEY, String(width));
        } catch  {
        // localStorage access failed - silently ignore
        }
    }, []);
    // Handle manual resize via drag
    useEffect(()=>{
        let dragShield = null;
        const removeDragShield = ()=>{
            dragShield?.remove();
            dragShield = null;
        };
        const handleMouseMove = (e)=>{
            if (!isResizingRef.current) return;
            const deltaX = startXRef.current - e.clientX;
            let newWidth = startWidthRef.current + deltaX;
            // Clamp width between min and max, respecting viewport constraints
            const viewportWidth = window.innerWidth;
            newWidth = clampWidth(newWidth, minWidth, maxWidth, responsive, viewportWidth);
            currentWidthRef.current = newWidth;
            onWidthChange?.(newWidth);
        };
        const handleMouseUp = ()=>{
            isResizingRef.current = false;
            removeDragShield();
            // Persist final width
            persistWidth(currentWidthRef.current);
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
        const handleMouseDown = (e)=>{
            if (e.button !== 0) return; // Only left click
            e.preventDefault(); // Prevent text selection during drag
            e.stopPropagation(); // Stop event from bubbling
            isResizingRef.current = true;
            startXRef.current = e.clientX;
            // CSS can cap the panel below its requested width. Start from the
            // visible width so reversing a drag responds immediately.
            startWidthRef.current = handleElement?.parentElement?.getBoundingClientRect().width || currentWidthRef.current;
            // An embedded editor owns mouse events inside its iframe. Cover it only
            // during the drag so move/up stay in this document in both directions.
            removeDragShield();
            dragShield = document.createElement('div');
            dragShield.setAttribute('aria-hidden', 'true');
            Object.assign(dragShield.style, {
                position: 'fixed',
                inset: '0',
                zIndex: '2147483647',
                cursor: 'col-resize',
                userSelect: 'none'
            });
            document.body.appendChild(dragShield);
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
        };
        if (!handleElement) return;
        handleElement.addEventListener('mousedown', handleMouseDown);
        return ()=>{
            removeDragShield();
            isResizingRef.current = false;
            handleElement.removeEventListener('mousedown', handleMouseDown);
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [
        handleElement,
        minWidth,
        maxWidth,
        responsive,
        onWidthChange,
        persistWidth
    ]);
    // Handle window resize with debouncing
    useEffect(()=>{
        const handleWindowResize = ()=>{
            // Clear existing timeout
            if (resizeTimeoutRef.current) {
                clearTimeout(resizeTimeoutRef.current);
            }
            // Debounce resize handling
            resizeTimeoutRef.current = setTimeout(()=>{
                const viewportWidth = window.innerWidth;
                const maxAllowed = getMaxAllowedWidth(viewportWidth, responsive, maxWidth);
                // Only adjust if current width exceeds max allowed
                if (currentWidthRef.current > maxAllowed) {
                    const newWidth = clampWidth(currentWidthRef.current, minWidth, maxWidth, responsive, viewportWidth);
                    currentWidthRef.current = newWidth;
                    onWidthChange?.(newWidth);
                    persistWidth(newWidth);
                }
            }, RESIZE_DEBOUNCE_MS);
        };
        window.addEventListener('resize', handleWindowResize);
        return ()=>{
            window.removeEventListener('resize', handleWindowResize);
            if (resizeTimeoutRef.current) {
                clearTimeout(resizeTimeoutRef.current);
            }
        };
    }, [
        minWidth,
        maxWidth,
        responsive,
        onWidthChange,
        persistWidth
    ]);
    return resizeHandleRef;
};
